import { Injectable } from '@angular/core';
import { defer, EMPTY, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, mapTo, mergeMap, tap } from 'rxjs/operators';
import { ArrayHelper } from '../../../helpers/arrayHelper';
import { FileHelper } from '../../../helpers/fileHelper';
import { EApplicationEventType } from '../../../model/application/EApplicationEventType';
import { ConfigData } from '../../../../../../apps/idl/src/model/config/ConfigData';
import { EPrefix } from '../../../../../../apps/idl/src/model/EPrefix';
import { EFileError } from '../../../../../../apps/idl/src/model/file/EFileError';
import { IFileError } from '../../../../../../apps/idl/src/model/file/IFileError';
import { EDatabaseRole } from '../../../model/store/EDatabaseRole';
import { EStoreFlag } from '../../../model/store/EStoreFlag';
import { IDataSource } from '../../../model/store/IDataSource';
import { IStoreDataResponse } from '../../../model/store/IStoreDataResponse';
import { ApplicationService } from '../../../services/security/application.service';
import { ShowMessageParamsPopup } from '../../../services/security/interfaces/ShowMessageParamsPopup';
import { PlatformService } from '../../../services/security/platform.service';
import { Store } from '../../../services/store.service';
import { UiMessageService } from '../../../services/security/uiMessage.service';
import { FilesystemService } from '../../filesystem/services/filesystem.service';
import { DmsFile } from '../model/DmsFile';
import { IDms } from '../model/IDms';
import { IDmsData } from '../model/IDmsData';
import { IDmsEvent } from '../model/IDmsEvent';
import { IDmsEventData } from '../model/IDmsEventData';
import { IDmsMeta } from '../model/IDmsMeta';

/** Service de gestion des fichiers sur le système de fichier en mode local uniquement. */
@Injectable({ providedIn: "root" })
export class LocalDmsService implements IDms {

	//#region FIELDS

	/** "DMS" */
	public static readonly C_DMS = "DMS";
	/** Identifiant de log pour le service. */
	private static readonly C_LOG_ID = "LDMS.S::";

	/** Identifiant de la base de données du DMS. */
	private C_DMS_DATABASE_ID: string;

	/** Sujet pour l'envoi d'événement. */
	private moEventSubject = new Subject<IDmsEvent>();

	//#endregion

	//#region METHODS

	constructor(
		/** Service des requêtes en base de données. */
		private isvcStore: Store,
		private readonly isvcFilesystem: FilesystemService,
		/** Service de gestion des popups et toasts. */
		private isvcUiMessage: UiMessageService,
		private isvcPlatorm: PlatformService,
		psvcApplication: ApplicationService
	) {
		this.init(psvcApplication);
	}

	/** Initialisation du service qui se sert de données de config.
	 * @param psvcApplication Service de gestion de l'application.
	 */
	private init(psvcApplication: ApplicationService): void {
		psvcApplication.waitForFlag(EStoreFlag.DBInitialized, true)
			.pipe(
				tap(_ => this.C_DMS_DATABASE_ID = ArrayHelper.getFirstElement(this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.dmsStorage))),
				mergeMap(_ => {
					this.initPaths();

					if (this.isvcPlatorm.isMobileApp) {
						return this.initDirectoryCreation(ConfigData.environment.dms.applicationPath)
							.pipe(mergeMap(__ => this.initDirectoryCreation(ConfigData.environment.dms.publicPath)));
					}
					else {
						console.warn(`${LocalDmsService.C_LOG_ID}Local DMS initialization failed: unsupported "${this.isvcPlatorm.model}" model / "${this.isvcPlatorm.platform}" platform.`);
						return EMPTY;
					}
				}),
				tap(_ => this.raiseReadyEvent())
			)
			.subscribe();
	}

	/** Initialise les chemins du dms local et retourne l'objet contenant ces chemins, `undefined` si pas de dms local. */
	private initPaths(): void {
		if (this.isvcPlatorm.isMobileApp) {
			ConfigData.environment.dms.applicationPath = `${LocalDmsService.C_DMS}/`;
			ConfigData.environment.dms.publicPath = `${ConfigData.appInfo.appName}/`;
		}
	}

	/** Crée un répertoire du DMS lors de l'initialisation du service.
	 * @param psPath Chemin d'accès jusqu'au dossier qu'on veut créer.
	 */
	private initDirectoryCreation(psPath: string): Observable<string> {
		return defer(() => this.isvcFilesystem.createDirectoryAsync(psPath))
			.pipe(
				catchError((poError: IFileError) => {
					if (poError.errorCode !== EFileError.DirectoryAlreadyExist) {
						this.isvcUiMessage.showMessage(new ShowMessageParamsPopup({ message: `Erreur lors de la création du dossier ${psPath}.`, header: "Erreur" }));

						console.error(`${LocalDmsService.C_LOG_ID}Erreur création répertoire '${psPath}' :`, poError);
						return throwError(poError);
					}
					else
						return of(undefined);
				})
			);
	}

	/** Envoie un événement pour notifier que le dmsLocal est prêt. */
	private raiseReadyEvent(): void {
		this.moEventSubject.next(this.createIDmsEvent({ isDmsReady: true }));
	}

	/** Crée et retourne un événement du DMS.
	 * @param poData Données de l'événement.
	 */
	private createIDmsEvent(poData: IDmsEventData): IDmsEvent {
		return {
			type: EApplicationEventType.DmsEvent,
			createDate: new Date(),
			data: poData
		};
	}

	/** Supprime le dossier qui contient le fichier portant le même identifiant.
	 * @param psId Identifiant du fichier à supprimer.
	 */
	public delete(psId: string): Observable<boolean> {
		return defer(() => this.isvcFilesystem.removeAsync(`${ConfigData.environment.dms.applicationPath}${psId}`))
			.pipe(
				// Si dossier non trouvé car déjà supprimé ou jamais téléchargé on continue normalement sinon on lève une erreur.
				catchError((poError: IFileError) => poError.errorCode === EFileError.DirectoryNotFound ? of(true) : throwError(poError)),
				mergeMap(_ => this.isvcStore.delete(psId, this.C_DMS_DATABASE_ID)),
				map((poResult: IStoreDataResponse) => poResult.ok)
			);
	}

	/** Récupération d'un fichier et ses méta stockés en base de données à partir d'un identifiant.
	 * @param psId Identifiant du fichier à récupérer.
	 */
	public get(psId: string): Observable<IDmsData> {
		return this.getMeta(psId)
			.pipe(
				catchError(poError => throwError(poError)),
				map((poResult: IDmsMeta) => {
					const loDmsFile: DmsFile = poResult ? new DmsFile(poResult) : null;
					return { file: loDmsFile, meta: poResult } as IDmsData;
				})
			);
	}

	/** Récupération d'un document de méta du DMS à partir de son identifiant, `null` si non trouvé.
	 * @param psId Identifiant du DmsMeta à récupérer.
	 */
	public getMeta(psId: string): Observable<IDmsMeta> {
		const loParams: IDataSource = {
			databasesIds: this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.dmsStorage),
			viewParams: {
				key: psId,
				include_docs: true
			}
		};

		return this.isvcStore.getOne<IDmsMeta>(loParams, false)
			.pipe(catchError(poError => { console.error(`${LocalDmsService.C_LOG_ID}Erreur récupération document dmsMeta ${psId} : `, poError); return throwError(poError); }));
	}

	/** Récupération d'un tableau de document de méta du DMS à partir d'un tableau d'identifiants, `null` si non trouvé.
	 * @param paIds Tableau d'identifiants du DmsMeta à récupérer.
	 */
	public getMetas(paIds: string[]): Observable<IDmsMeta[]> {
		const loParams: IDataSource = {
			databasesIds: this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.dmsStorage),
			viewParams: {
				keys: paIds,
				include_docs: true
			}
		};

		return this.isvcStore.get<IDmsMeta>(loParams)
			.pipe(tap(_ => { }, poError => console.error(`${LocalDmsService.C_LOG_ID}Erreur récupération tableau documents dmsMeta ${paIds.join(", ")} : `, poError)));
	}

	/** Enregistre un objet DmsFile dans le dossier DMS du système de fichiers.
	 * @param poData Objet contenant le fichier sous forme de Blob ou base64 ainsi que son nom, sa taille, son mimeType.
	 * @param poMeta Méta du fichier à enregistrer.
	 */
	public save(poData: DmsFile, poMeta: IDmsMeta): Observable<IDmsMeta> {
		const lsFileExtension: string | undefined = FileHelper.getFileExtensionFromFileName(poData.Name);
		const lsFileName = lsFileExtension ? `${poMeta._id}.${lsFileExtension}` : poMeta._id;
		const lsPath = `${ConfigData.environment.dms.applicationPath}${poMeta._id}/${lsFileName}`;

		return defer(() => this.saveFile(lsPath, poData, poMeta))
			.pipe(
				catchError((poError: IFileError) => {
					console.error(`${LocalDmsService.C_LOG_ID}Erreur d'enregistrement du fichier ${poMeta.name} :`, poError);
					return throwError(poError);
				})
			);
	}

	/** Enregistre un fichier base64 et son méta associé sur le système de fichiers.
	 * @param psResultPath Chemin vers le dossier nouvellement créé.
	 * @param poData Blob ou objet contenant le fichier sous forme de base64 ainsi que son nom, sa taille, son mimeType.
	 * @param poDmsMeta Objet méta associé au fichier à enregistrer.
	 */
	private saveFile(psResultPath: string, poData: DmsFile, poDmsMeta: IDmsMeta): Observable<IDmsMeta> {
		return defer(() => { poDmsMeta.saved = false; return of(null); })
			.pipe(
				mergeMap(_ => this.saveMeta(poDmsMeta)),
				mergeMap(_ => this.isvcFilesystem.createFileAsync(psResultPath, poData.File)),
				tap((psPath: string) => {
					poData.Path = psPath;
					poDmsMeta.saved = true;
				}),
				mergeMap(_ => this.saveMeta(poDmsMeta)),
				tap((poResult: IDmsMeta) => {
					console.debug(`${LocalDmsService.C_LOG_ID}metadocument '${poResult._id}' SAVED`);
					this.moEventSubject.next(this.createIDmsEvent({ fileId: poResult._id, dmsFile: new DmsFile(poData.File, poDmsMeta), dmsMeta: poResult }));
				})
			);
	}

	/** L'enregistrement du fichier en local a réussi, on enregistre en base de données le fichier méta associé.
	 * @param poDmsMeta Objet en réponse à l'enregistrement du fichier.
	 * @param psPath Chemin d'accès jusqu'au fichier créé.
	 */
	public saveMeta(poDmsMeta: IDmsMeta): Observable<IDmsMeta> {
		return this.isvcStore.put(poDmsMeta, this.C_DMS_DATABASE_ID, true)
			.pipe(
				catchError(poError => {
					console.error(`${LocalDmsService.C_LOG_ID}Erreur lors put() du dmsMeta : `, poDmsMeta, "Erreur : ", poError);
					this.isvcUiMessage.showMessage(
						new ShowMessageParamsPopup({
							header: "Erreur",
							message: `Erreur lors de l'enregistrement en base de données des informations sur le fichier ${poDmsMeta.name}.`
						})
					);
					return throwError(poError);
				}),
				mapTo(poDmsMeta)
			);
	}

	/** Permet de s'abonner aux événements du LocalDmsService.
	 * @param pfNext Fonction appelée lors du next => function(poResult: IDmsEvent).
	 * @param pfError Fonction appelée lors du error => function(poError: Any).
	 */
	public subscribe(pfNext: Function, pfError?: Function): void {
		this.moEventSubject.asObservable()
			.subscribe(
				(poResult: IDmsEvent) => pfNext(poResult),
				poError => pfError(poError)
			);
	}

	/** Récupère toutes les données des fichiers présents dans le système de fichiers de l'appareil.
	 * @param pbLive Indique si on fait une récupération continue ou non, `false` par défaut.
	 */
	public getLocalMetaDataFiles(pbLive: boolean = false): Observable<IDmsMeta[]> {
		const loDatasouce: IDataSource = {
			databaseId: this.C_DMS_DATABASE_ID,
			viewParams: {
				startkey: EPrefix.dms,
				endkey: `${EPrefix.dms}${Store.C_ANYTHING_CODE_ASCII}`,
				include_docs: true
			},
			live: pbLive
		};

		return this.isvcStore.get<IDmsMeta>(loDatasouce);
	}

	//#endregion
}